import React from "react";

interface JsonDisplayProps {
  className?: string;
  json: unknown;
  emptyMessage?: string;
  backgroundColor?: string;
  keyColor?: string;
  valueColor?: string;
  fontSize?: string;
}

const DEFAULT_BACKGROUND_COLOR = "#fafafa";
const DEFAULT_KEY_COLOR = "#1f8efc";
const DEFAULT_VALUE_COLOR = "#ad6743";
const STRUCTURAL_COLOR = "#000000"; // Applies a black color to structural characters like brackets and commas
const STRUCTURAL_CHARS = ["{", "}", "[", "]", ","];
const INDENT_SIZE = 2; // Number of spaces per indent level in JSON.stringify

const JsonDisplay: React.FC<JsonDisplayProps> = ({
  className,
  json,
  emptyMessage,
  backgroundColor = DEFAULT_BACKGROUND_COLOR,
  keyColor = DEFAULT_KEY_COLOR,
  valueColor = DEFAULT_VALUE_COLOR,
  fontSize = "12px",
}) => {
  // Early return for null/undefined json, with optional empty message
  if (!json) return emptyMessage ? <div>{emptyMessage}</div> : null;

  // HELPER FUNCTION - Splits text into colored spans, handling structural chars separately
  const formatText = (text: string, defaultColor: string): JSX.Element[] => {
    let formatted: JSX.Element[] = [];
    let currentText = "";
    let currentColor = defaultColor;
    let inString = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (char === '"' && (i === 0 || text[i - 1] !== "\\")) {
        inString = !inString; // Toggle string state, ignoring escaped quotes
      }

      if (STRUCTURAL_CHARS.includes(char) && !inString) {
        if (currentText) {
          formatted.push(
            <span key={i} style={{ color: currentColor }}>
              {currentText}
            </span>
          );
          currentText = "";
        }
        formatted.push(
          <span key={i} style={{ color: STRUCTURAL_COLOR }}>
            {char}
          </span>
        );
      } else {
        currentText += char;
      }
    }

    if (currentText) {
      formatted.push(
        <span key={text.length} style={{ color: currentColor }}>
          {currentText}
        </span>
      );
    }

    return formatted;
  };

  // PRIMARY FUNCTION - Formats JSON into JSX elements with proper indentation
  const formatJson = (obj: unknown): JSX.Element[] | JSX.Element => {
    try {
      const formattedJson = JSON.stringify(obj, null, INDENT_SIZE);
      const lines = formattedJson.split("\n");

      // Skip first and last lines like '{' and '}' (for top-level objects)
      const contentLines = lines.slice(1, -1);

      // Check if content is effectively empty (only whitespace or no lines)
      const isEmptyContent = contentLines.every((line) => !line.trim());

      if (isEmptyContent && lines.length <= 2) {
        // Render "No Value" when there's no meaningful content
        return <div>No Value</div>;
      }

      const formattedLines = lines // Changed from contentLines to lines to include opening/closing brackets
        .map((line, index) => {
          const leadingSpaces = line.match(/^\s*/)?.[0].length || 0;
          const indentLevel = leadingSpaces / INDENT_SIZE; // No -1 since we're keeping all lines
          const trimmedLine = line.trim();
          if (!trimmedLine) return null; // Skip empty lines

          // Handle structural lines (e.g., "{", "}", "[", "]")
          if (
            trimmedLine === "{" ||
            trimmedLine === "}" ||
            trimmedLine === "[" ||
            trimmedLine === "]"
          ) {
            return (
              <div
                key={index}
                style={{ marginLeft: `${indentLevel * INDENT_SIZE}em` }}
              >
                {formatText(trimmedLine, STRUCTURAL_COLOR)}
              </div>
            );
          }

          const parts = trimmedLine.split(":");
          if (parts.length === 2 && trimmedLine.includes(":")) {
            const keyPart = parts[0].trim().replace(/"/g, "");
            const valuePart = parts[1].trim();

            return (
              <div
                key={index}
                style={{ marginLeft: `${indentLevel * INDENT_SIZE}em` }} // em for font-size-based indent
              >
                <span style={{ color: keyColor }}>"{keyPart}"</span>:{" "}
                {formatText(valuePart, valueColor)}
              </div>
            );
          }

          return (
            <div
              key={index}
              style={{ marginLeft: `${indentLevel * INDENT_SIZE}em` }}
            >
              {formatText(trimmedLine, valueColor)}
            </div>
          );
        })
        .filter((line): line is JSX.Element => line !== null); // Type guard removes nulls

      return formattedLines;
    } catch (error) {
      return (
        <div style={{ color: "red" }}>
          There is an error displaying this data.
        </div>
      );
    }
  };

  // RENDER -------------------------------------------------------------------------------
  return (
    <div
      className={className}
      style={{
        backgroundColor,
        padding: "16px",
      }}
    >
      <pre
        style={{
          margin: 0,
          fontFamily: "monospace",
          fontSize,
          lineHeight: "1.5", // Unitless, scales with fontSize
        }}
      >
        {formatJson(json)}
      </pre>
    </div>
  );
};

export default JsonDisplay;
